import { useEffect, useRef } from 'react';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { runInAction } from 'mobx';
import PropTypes from 'prop-types';
import Sms from './components/Confirmations/Sms';
import Google2FA from './components/Confirmations/Google2FA';
import SwitchConfirmationMethodScreen from './components/SwitchConfirmationMethodScreen';
import PushNotification2FA from './components/Confirmations/PushNotification2FA';
import { PASSWORD_RECOVERY_STATUSES, TWO_FACTOR_AUTH_TYPES } from 'components/common/constants';
import { clearLocalStorage, getAvailableConfirmationTypes } from 'services/authUtils';
import { loginPin } from 'services/requestAgent';

const ActionConfirmation = ({ authStore, userStore, passwordRecoveryStore, isPasswordRecovery = false }) => {
  // Anonymous flow is for login/registration, when user is not yet authenticated
  const isAnonymousFlow = !userStore.isInitialized;
  const pushPollingIntervalRef = useRef(null);
  const store = isPasswordRecovery ? passwordRecoveryStore : authStore;

  useEffect(() => {
    // Start polling for push notification confirmation
    if (
      isAnonymousFlow &&
      store.confirmationActionType === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION &&
      store.confirmationId
    ) {
      pushPollingIntervalRef.current = setInterval(async () => {
        try {
          // Call confirmation endpoint with null code for push notification
          // Backend will respond with CONFIRMATION_PENDING_ACTION until mobile confirms
          const response = await loginPin(null, store.confirmationId);

          if (response.success) {
            clearInterval(pushPollingIntervalRef.current);
            store.setIsUserAlreadyRegistered(false);
            store.setIsPinSuccess(true);
          }
        } catch (error) {
          // Check for specific error codes
          if (error?.code === 'CONFIRMATION_REJECTED') {
            clearInterval(pushPollingIntervalRef.current);
            // Reset to login form using runInAction for proper MobX updates
            runInAction(() => {
              store.confirmationActionType = null;
              store.confirmationId = null;
              if (isPasswordRecovery) {
                // Reset password recovery flow
                store.recoveryStatus = null;
                store.error = {
                  code: 'CONFIRMATION_REJECTED',
                  message: 'Push notification was rejected. Please try again.'
                };
              } else {
                // Reset login flow
                store.isLoginSuccess = false;
                store.isPushNotificationEnabled = false;
                store.error = {
                  code: 'CONFIRMATION_REJECTED',
                  message: 'Push notification was rejected. Please try again.'
                };
              }
            });
          } else if (error?.code === 'CONFIRMATION_PENDING_ACTION') {
            // Continue polling, mobile hasn't confirmed yet
          } else {
            // Handle other errors
            // eslint-disable-next-line no-console
            console.error('Error during push confirmation:', error);
          }
        }
      }, 2000); // Poll every 2 seconds
    }

    return () => {
      if (pushPollingIntervalRef.current) {
        clearInterval(pushPollingIntervalRef.current);
      }
    };
  }, [isAnonymousFlow, store, store.confirmationActionType, store.confirmationId]);

  if (isAnonymousFlow) {
    const handleCode = (code, disableSend) => {
      store.setCode(code);
      if (!disableSend && !store.isLoading) {
        if (isPasswordRecovery) {
          if (store.recoveryStatus === PASSWORD_RECOVERY_STATUSES.PHONE_CONFIRM) {
            store.sendPhoneCode();
          }
        } else {
          store.sendCode();
        }
      }
    };
    const resendSecurityCode = () => {
      if (isPasswordRecovery) {
        if (store.recoveryStatus === PASSWORD_RECOVERY_STATUSES.PHONE_CONFIRM) {
          store.resendPhoneCode();
        }
      } else {
        store.resendSecurityCode();
      }
    };

    if (store.isShowSwitchConfirmationMethodScreen) {
      return (
        <SwitchConfirmationMethodScreen
          onSwitchMethod={store.forceSwitchToAnotherConfirmationType}
          onBack={() => store.setIsShowSwitchConfirmationMethodScreen(false)}
          currentConfirmationType={store.confirmationActionType}
          availableConfirmationTypes={getAvailableConfirmationTypes()}
        />
      );
    } else if (store.confirmationActionType === TWO_FACTOR_AUTH_TYPES.GOOGLE_AUTH) {
      return (
        <Google2FA sms={store.values.code} error={store.error} handleSms={handleCode} resendCode={resendSecurityCode} />
      );
    } else if (store.confirmationActionType === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION) {
      return <PushNotification2FA />;
    } else {
      const phone = isPasswordRecovery ? store.generalFields.phoneNumber : store.phone;

      return (
        <Sms
          phone={phone}
          sms={store.values.code}
          error={store.error}
          handleSms={handleCode}
          resendCode={resendSecurityCode}
          isForgotPassword={isPasswordRecovery}
        />
      );
    }
  }

  return null;
};

ActionConfirmation.propTypes = {
  authStore: MobXPropTypes.observableObject,
  userStore: MobXPropTypes.observableObject,
  passwordRecoveryStore: MobXPropTypes.observableObject,
  isPasswordRecovery: PropTypes.bool
};

export default inject('authStore', 'userStore', 'passwordRecoveryStore')(observer(ActionConfirmation));
